import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { betreuungAPI } from '@/api/betreuungAPI';

// 29.10.2026: Betreuerwechsel ohne historischen Beginn -- Status "noch zu klaeren", Nachtrag mit Herkunft (nie Wechsel-/Wirksamkeitsdatum)
export const BEGINN_UNBEKANNT_HINWEIS = 'Ursprünglicher Beginn der Betreuung unbekannt. Bitte ursprünglichen Bestellungsbeschluss prüfen oder Datum beim Betreuungsgericht erfragen.';
export const beginnOffen = (b) => !!b && !b.betreuung_seit && b.betreuung_seit_status === 'noch_zu_klaeren';

// Gerichtlicher Beschluss (25.10.2026): Gericht, Aktenzeichen, Wirksamkeit, Ueberpruefung, Einwilligungsvorbehalt und Betreuerwechsel aus dem
// Betreuungsdatensatz -- war gespeichert, aber in der Akte nirgends sichtbar. Offene Angaben werden ausdruecklich als offen angezeigt.
const de = (iso) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : iso || null);
const EVB = { fortbestehend: 'besteht fort (Umfang laut Vorbeschluss – offen)', angeordnet: 'angeordnet', nicht_angeordnet: 'nicht angeordnet' };
export const AK_KATEGORIE = {
  vermoegenssorge: 'Vermögenssorge', gesundheitssorge: 'Gesundheitssorge', aufenthaltsbestimmung: 'Aufenthaltsbestimmung',
  wohnungsangelegenheiten: 'Wohnungsangelegenheiten', behoerdenangelegenheiten: 'Behörden', post_fernmeldeverkehr: 'Post/Fernmeldeverkehr',
  vertretung_vor_gericht: 'Vertretung vor Gericht',
};

export function beschlussZeilen(b) {
  if (!b) return [];
  const evb = b.einwilligungsvorbehalt_status ? EVB[b.einwilligungsvorbehalt_status] : (b.einwilligungsvorbehalt_belegt ? (b.einwilligungsvorbehalt ? 'ja' : 'nein') : null);
  return [
    ['Betreuungsgericht', b.gericht],
    ['Aktenzeichen', b.aktenzeichen],
    ['Beschluss vom', de(b.letzter_beschluss_datum)],
    ['Wirksam ab', de(b.wirksam_ab || b.bestellung_am)],
    ['Überprüfung bis', de(b.naechste_pruefung)],
    ['Einwilligungsvorbehalt', evb],
    ['Betreuung insgesamt seit (VBVG)', de(b.betreuung_seit) || (beginnOffen(b) ? 'noch zu klären' : null)],
    ...(b.bisheriger_betreuer ? [['Früherer Betreuer', `${b.bisheriger_betreuer} (bis ${de(b.wirksam_ab) || 'unbekannt'})`]] : []),
    ...(b.bestellter_betreuer ? [['Bestellter Betreuer', `${b.bestellter_betreuer} (seit ${de(b.wirksam_ab) || 'unbekannt'})`]] : []),
  ].map(([label, wert]) => ({ label, wert: wert || null }));
}

function BeginnNachtrag({ betreuung, onGespeichert }) {
  const [datum, setDatum] = useState('');
  const [herkunft, setHerkunft] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const speichern = async () => {
    setLaeuft(true);
    try {
      await betreuungAPI.neueBetreuungBeginnNachtragen(betreuung.id, { datum, herkunft });
      toast.success('Beginn der Betreuung nachgetragen (mit Herkunft).');
      if (onGespeichert) onGespeichert();
    } catch (e) { toast.error(e.message); }
    setLaeuft(false);
  };
  return (
    <div className="sm:col-span-2 border border-amber-300 bg-amber-50 rounded p-2 space-y-1 text-xs" data-testid="beginn-nachtrag">
      <div className="text-amber-900" data-testid="beginn-hinweis">{BEGINN_UNBEKANNT_HINWEIS}</div>
      <div className="flex flex-wrap gap-2 items-center">
        <Input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} className="h-8 w-40" data-testid="beginn-datum" />
        <Input value={herkunft} onChange={(e) => setHerkunft(e.target.value)} placeholder="Herkunft, z. B. Bestellungsbeschluss AG … vom … / Auskunft Gericht am …"
          className="h-8 flex-1 min-w-[16rem]" data-testid="beginn-herkunft" />
        <Button size="sm" onClick={speichern} disabled={laeuft || !datum || herkunft.trim().length < 5} data-testid="beginn-speichern">Nachtragen</Button>
      </div>
    </div>
  );
}

export default function BeschlussKarte({ betreuung, onGespeichert }) {
  const zeilen = beschlussZeilen(betreuung);
  if (!zeilen.length) return null;
  return (
    <Card data-testid="beschluss-karte">
      <CardHeader className="pb-2"><CardTitle className="text-base">Gerichtlicher Beschluss</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
        {zeilen.map((z) => (
          <div key={z.label} className="flex justify-between gap-2 border-b border-slate-100 py-1" data-testid={`beschluss-${z.label}`}>
            <span className="text-slate-500">{z.label}</span>
            <span className={z.wert ? 'text-slate-800 text-right' : 'text-amber-700 text-right'}>{z.wert || 'offen – nicht belegt'}</span>
          </div>
        ))}
        {beginnOffen(betreuung) && <BeginnNachtrag betreuung={betreuung} onGespeichert={onGespeichert} />}
      </CardContent>
    </Card>
  );
}
